import React from "react";
import { AlbumComment } from "../../types/album";
import { MediaComment } from "../../types/media";
import OverlayRoot from "./OverlayRoot";

interface DeleteCommentModalProps {
  isOpen: boolean;
  comment: AlbumComment | MediaComment | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

const DeleteCommentModal: React.FC<DeleteCommentModalProps> = ({
  isOpen,
  comment,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  if (!isOpen || !comment) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <OverlayRoot data-section="delete-comment-modal-root"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overscroll-none"
      onClick={handleBackdropClick}
    >
      <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full mx-4 border border-white/20 shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white">Delete Comment</h3>
        </div>

        {/* Content */}
        <div className="mb-8">
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            Are you sure you want to delete this comment?
          </p>
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10 mb-4">
            <p className="text-gray-100 text-sm leading-relaxed line-clamp-3">
              "{comment.content}"
            </p>
          </div>
          <p className="text-gray-400 text-sm">
            This action cannot be undone.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 px-6 py-3 text-white bg-gray-600/50 hover:bg-gray-600/70 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500/50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting}
            className="flex-1 px-6 py-3 text-white bg-red-500 hover:bg-red-600 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500/50 flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete Comment
              </>
            )}
          </button>
        </div>
      </div>
    </OverlayRoot>
  );
};

export default DeleteCommentModal;